import { useState, useCallback } from 'react';
import { BmiSuggestionsModal } from '@/components/BmiSuggestionsModal';
import { Strings } from '@/constants/strings';

type BmiTier = 'underweight' | 'normal' | 'overweight' | 'obese';

type ObeseClass = 1 | 2 | 3;

const BMI_CATEGORIES: { max: number; label: string; tier: BmiTier; obeseClass?: ObeseClass }[] = [
  { max: 18.5, label: Strings.bmiUnderweight, tier: 'underweight' },
  { max: 24.9, label: Strings.bmiNormal, tier: 'normal' },
  { max: 29.9, label: Strings.bmiOverweight, tier: 'overweight' },
  { max: 34.9, label: Strings.bmiObese1, tier: 'obese', obeseClass: 1 },
  { max: 39.9, label: Strings.bmiObese2, tier: 'obese', obeseClass: 2 },
  { max: Infinity, label: Strings.bmiObese3, tier: 'obese', obeseClass: 3 },
];

function getBmiResult(bmi: number): { label: string; tier: BmiTier; obeseClass?: ObeseClass } {
  for (const cat of BMI_CATEGORIES) {
    if (bmi < cat.max) return { label: cat.label, tier: cat.tier, obeseClass: cat.obeseClass };
  }
  return { label: Strings.bmiObese3, tier: 'obese', obeseClass: 3 };
}

const BMI_COLORS: Record<BmiTier, { value: string; text: string }> = {
  underweight: { value: 'text-amber-600', text: 'text-amber-700' },
  normal: { value: 'text-[#22c55e]', text: 'text-[#22c55e]' },
  overweight: { value: 'text-orange-500', text: 'text-orange-600' },
  obese: { value: 'text-red-600', text: 'text-red-600' },
};


interface BmiCalculatorProps {
  onBmiCalculated?: (bmi: number) => void;
}

export function BmiCalculator({ onBmiCalculated }: BmiCalculatorProps) {
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [result, setResult] = useState<{ bmi: number; label: string; tier: BmiTier; obeseClass?: ObeseClass } | null>(
    null
  );
  const [heightError, setHeightError] = useState('');
  const [weightError, setWeightError] = useState('');
  const [suggestionsModalOpen, setSuggestionsModalOpen] = useState(false);

  const handleCalculate = useCallback(() => {
    const h = parseFloat(height.replace(',', '.'));
    const w = parseFloat(weight.replace(',', '.'));

    setHeightError('');
    setWeightError('');

    if (Number.isNaN(h) || h <= 0 || h > 300) {
      setHeightError('Geçerli bir boy girin (1–300 cm)');
      return;
    }
    if (Number.isNaN(w) || w <= 0 || w > 500) {
      setWeightError('Geçerli bir kilo girin (1–500 kg)');
      return;
    }

    const heightM = h / 100;
    const bmi = w / (heightM * heightM);
    setResult({ bmi, ...getBmiResult(bmi) });
    onBmiCalculated?.(bmi);
  }, [height, weight, onBmiCalculated]);

  const handleNewCalculation = useCallback(() => {
    setResult(null);
    setHeight('');
    setWeight('');
    setHeightError('');
    setWeightError('');
  }, []);

  if (result) {
    const colors = BMI_COLORS[result.tier];
    return (
      <div className="flex min-h-[calc(100vh-56px)] flex-1 flex-col bg-surface">
        <BmiSuggestionsModal
          visible={suggestionsModalOpen}
          tier={result.tier}
          obeseClass={result.obeseClass}
          onClose={() => setSuggestionsModalOpen(false)}
        />
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto flex max-w-md flex-col space-y-6">
            <div className="overflow-hidden rounded-3xl border border-slate-100 bg-card-bg py-8 px-6 shadow-sm">
              <h2 className="mb-4 text-center text-xl font-semibold text-text-heading">
                {Strings.bmiYourResult}
              </h2>
              <p className={`mb-2 text-center text-5xl font-bold ${colors.value}`}>
                {result.bmi.toFixed(1)}
              </p>
              <p className={`text-center text-base font-medium ${colors.text}`}>
                {result.label}
              </p>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => setSuggestionsModalOpen(true)}
                  className="rounded-xl bg-[#87ceeb] px-6 py-3 text-base font-semibold text-slate-800 transition hover:bg-[#7bc4e8] active:bg-[#6fb8de]"
                >
                  Önerileri Gör
                </button>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={handleNewCalculation}
                className="w-full rounded-2xl bg-primary py-4 text-lg font-bold text-white transition hover:bg-primary-active active:bg-primary-active"
              >
                {Strings.bmiNewCalculation}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-56px)] flex-1 flex-col bg-surface">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-md flex-col space-y-6">
          <p className="text-center text-base text-text-muted">
            Boy ve kilonuzu girin. BMI değeriniz hesaplanacaktır.
          </p>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="bmi-height"
                className="mb-1 block text-sm font-medium text-text-heading"
              >
                {Strings.bmiHeightLabel}
              </label>
              <input
                id="bmi-height"
                type="number"
                inputMode="decimal"
                min={1}
                max={300}
                step={0.1}
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="örn: 175"
                className="w-full min-h-[48px] rounded-xl border-2 border-slate-200 bg-card-bg px-4 py-3.5 text-base text-text-heading placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
              <p className="mt-1 text-xs text-text-muted">
                {Strings.bmiHeightHint}
              </p>
              {heightError && (
                <p className="mt-1 text-xs text-red-600" role="alert">
                  {heightError}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="bmi-weight"
                className="mb-1 block text-sm font-medium text-text-heading"
              >
                {Strings.bmiWeightLabel}
              </label>
              <input
                id="bmi-weight"
                type="number"
                inputMode="decimal"
                min={1}
                max={500}
                step={0.1}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="örn: 70"
                className="w-full min-h-[48px] rounded-xl border-2 border-slate-200 bg-card-bg px-4 py-3.5 text-base text-text-heading placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
              <p className="mt-1 text-xs text-text-muted">
                {Strings.bmiWeightHint}
              </p>
              {weightError && (
                <p className="mt-1 text-xs text-red-600" role="alert">
                  {weightError}
                </p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleCalculate}
            className="w-full rounded-2xl bg-primary py-4 text-lg font-bold text-white transition hover:bg-primary-active active:bg-primary-active"
          >
            {Strings.bmiCalculate}
          </button>
        </div>
      </div>
    </div>
  );
}
